import { getCase } from "../../content/lookups.js";
import { getMapNode } from "../../content/stages.js";
import { beginPhase } from "../run/beginPhase.js";
import { caseDeadline } from "../run/caseDeadline.js";
import { prepareCaseDeck } from "../run/prepareCaseDeck.js";
import type { Command, GameState, NodeId } from "../types/index.js";
import { appendLog } from "../run/log.js";
import { accept, reject } from "./reject.js";
import { openMapEvent } from "./mapEvent.js";
import { openRelicNode } from "./relicNode.js";

function openCase(state: GameState, nodeId: NodeId): GameState {
  const node = getMapNode(state.run?.stageId, nodeId);
  if (!node?.caseId || !state.run) return state;

  const caseDef = getCase(node.caseId);
  if (!caseDef) return reject(state, "未知の案件");
  const modifiers = state.run.nextCaseModifiers ?? { deadline: 0, required: 0, energy: 0 };

  let run = beginPhase({
    run: {
      ...prepareCaseDeck(state.run),
      nextCaseModifiers: { deadline: 0, required: 0, energy: 0 },
    },
    activeCase: {
      nodeId,
      caseId: node.caseId,
      progress: 0,
      pendingProgress: 0,
      required: Math.max(1, (node.required ?? caseDef.required) + modifiers.required),
      deadline: Math.max(1, caseDeadline(node.deadline ?? caseDef.deadline, state.run.organizations) + modifiers.deadline),
      nextEnergyModifier: modifiers.energy,
      usedFreeAction: false,
      lastFieldJudgment: null,
      phase: 1,
    },
    resetPhase: true,
  });

  run = appendLog(run, {
    phase: 1,
    kind: "system",
    message: `${caseDef.name}を開始`,
  });

  return accept({
    ...state,
    screen: "case.play",
    overlay: run.hasSeenPhaseTutorial ? "none" : "phaseTutorial",
    run,
    resolution: null,
  });
}

export function handleSelectNode(
  state: GameState,
  command: Extract<Command, { type: "SELECT_NODE" }>,
): GameState {
  if (state.screen !== "map" || !state.run) {
    return reject(state, "マップ画面でのみノードを選択できる");
  }
  if (!state.run.availableNodeIds.includes(command.nodeId)) {
    return reject(state, "到達できないノード");
  }

  const node = getMapNode(state.run.stageId, command.nodeId);
  if (!node) return reject(state, "未知のノード");

  if (node.kind === "rest") {
    return accept({
      ...state,
      screen: "rest",
      run: { ...state.run, currentNodeId: command.nodeId },
    });
  }

  if (node.kind === "event") return openMapEvent(state, command.nodeId);

  if (node.kind === "relic") return openRelicNode(state, command.nodeId);

  return openCase(state, command.nodeId);
}
